import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {ActionIcon, Box, Skeleton, Stack, Tooltip} from "@mantine/core";
import {useLocation} from "react-router-dom";
import {useMediaQuery} from "@mantine/hooks";
import {t} from "i18next";
import IconArrowLeft from "../../assets/arrow-left.svg?react";
import IconArrowRight from "../../assets/arrow-right.svg?react";
import IconArrowBarToUp from "../../assets/arrow-bar-to-up.svg?react";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {TableOfContents} from "./TableOfContents";
import type {DibDocument} from "./types";
import {buildTocTree} from "./tocUtils";
import classes from "./HtmlArticle.module.css";
import layoutClasses from "./ContentLayout.module.css";
import {RelatedContent} from "./RelatedContent";
import {SupplierInfo} from "./SupplierInfo";
import {renderNode} from "./renderNode";
import {useContentTitle} from "./useContentTitle";

/**
 * Renders descroot entries — the document's description/abstract.
 * Each descroot entry is a container element whose children are the
 * actual description content (typically <p> elements).
 */
const renderDescriptionContent = (doc: DibDocument) =>
    doc.descroot.flatMap((entry) => {
        const el = doc.elements[entry.id];
        if (!el?.children) return [];
        return el.children
            .filter((c): c is {id: string} => "id" in c)
            .map((c) => renderNode(doc, c.id, {}, c.id));
    });

const renderMainContent = (doc: DibDocument) => {
    return doc.root.map((entry) => renderNode(doc, entry.id, {}, entry.id));
};

const DocumentSkeleton = () => (
    <Stack gap="sm" pt="xl">
        <Skeleton height={32} width="60%" mb="md" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="95%" />
        <Skeleton height={14} width="88%" />
        <Skeleton height={14} width="92%" />
        <Skeleton height={14} width="40%" />
        <Skeleton height={24} width="45%" mt="xl" mb="xs" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="97%" />
        <Skeleton height={14} width="85%" />
        <Skeleton height={14} width="60%" />
    </Stack>
);

export const HTMLResource = ({
    resourceId,
    segmentId,
}: {
    resourceId: string;
    segmentId: string | undefined;
}) => {
    const {
        currentData: documentData,
        isFetching: isDocumentFetching,
        isError,
        error,
    } = dibAppApi.endpoints.getDocument.useQuery({resourceId, segmentId});

    // TOC document continuity — retain previous doc during segment transitions
    const prevDocRef = useRef<DibDocument | null>(null);
    if (documentData) {
        prevDocRef.current = documentData;
    }

    const isIntraTopicFetch =
        !documentData &&
        isDocumentFetching &&
        prevDocRef.current?.topicId === resourceId;
    const tocDocument =
        documentData ?? (isIntraTopicFetch ? prevDocRef.current : null);

    // Page title from the retained doc so same-topic segment fetches (which
    // blank currentData) don't flicker the title back to "KAI".
    useContentTitle(tocDocument?.name);

    const tocEntries = useMemo(() => {
        return tocDocument ? buildTocTree(tocDocument) : [];
    }, [tocDocument]);

    const showToc =
        (!isDocumentFetching || isIntraTopicFetch) && tocEntries.length > 0;

    // Scroll restoration
    const location = useLocation();
    const articleRef = useRef<HTMLElement>(null);

    // Click-override: track last-clicked TOC item to override :target-current
    const [clickedTocId, setClickedTocId] = useState<string | null>(null);

    // Reset override on new page navigation (setState during render, not in effect)
    const prevParamsRef = useRef<{
        resourceId: string | undefined | null;
        segmentId: string | undefined | null;
    }>({resourceId: null, segmentId: null});

    let isNewPage = false;
    if (documentData && !isDocumentFetching) {
        isNewPage =
            prevParamsRef.current.resourceId !== resourceId ||
            prevParamsRef.current.segmentId !== segmentId;
        if (isNewPage) {
            prevParamsRef.current = {resourceId, segmentId};
            const hash = location.hash?.slice(1) || null;
            if (clickedTocId !== hash) setClickedTocId(hash);
        }
    }

    // Scroll restoration: on new page navigation, instantly scroll to top
    // (or to hash anchor if present). Same-page hash navigation (e.g. TOC
    // clicks) is handled natively by the browser.
    const isNewPageRef = useRef(false);
    isNewPageRef.current = isNewPage;

    useLayoutEffect(() => {
        if (!isNewPageRef.current) return;

        const hash = location.hash?.slice(1);
        if (hash) {
            document
                .getElementById(hash)
                ?.scrollIntoView({behavior: "instant"});
        } else {
            articleRef.current?.scrollTo({top: 0, behavior: "instant"});
        }
    }, [
        documentData,
        isDocumentFetching,
        resourceId,
        segmentId,
        location.hash,
    ]);

    // Right sidebar collapse/expand
    const isDesktop = useMediaQuery("(min-width: 1080px)", undefined, {
        getInitialValueInEffect: false,
    });
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        () => !window.matchMedia("(min-width: 1080px)").matches
    );

    useEffect(() => {
        if (isDesktop === false) {
            setIsSidebarCollapsed(true);
        }
    }, [isDesktop]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed((prev) => !prev);
    }, []);

    // Clear click override on user scroll
    useEffect(() => {
        const clear = () => setClickedTocId(null);
        const inArticle = (e: Event) =>
            articleRef.current?.contains(e.target as Node);
        const handleWheel = (e: WheelEvent) => {
            if (inArticle(e)) clear();
        };
        const handleTouch = (e: TouchEvent) => {
            if (inArticle(e)) clear();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                inArticle(e) &&
                [
                    " ",
                    "ArrowUp",
                    "ArrowDown",
                    "PageUp",
                    "PageDown",
                    "Home",
                    "End",
                ].includes(e.key)
            )
                clear();
        };
        document.addEventListener("wheel", handleWheel, {passive: true});
        document.addEventListener("touchstart", handleTouch, {passive: true});
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("wheel", handleWheel);
            document.removeEventListener("touchstart", handleTouch);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const scrollToTop = useCallback((e: React.MouseEvent) => {
        e.currentTarget
            .closest("article")
            ?.scrollTo({top: 0, behavior: "smooth"});
    }, []);

    const getArticleContent = () => {
        if (isDocumentFetching && !documentData) {
            return <DocumentSkeleton />;
        }

        if (isError) {
            return <ResourceError error={error} />;
        }

        if (!documentData) {
            return (
                <StatusMessage
                    title="Empty document"
                    detail="This document has no content to display."
                />
            );
        }

        if (!documentData.access) {
            return (
                <StatusMessage
                    title="Access denied"
                    detail="Your subscription does not include access to this document."
                />
            );
        }

        if (documentData.root.length === 0) {
            return (
                <StatusMessage
                    title="Empty document"
                    detail="This document has no content to display."
                />
            );
        }

        const hasDescription = documentData.descroot?.length > 0;

        return (
            <>
                <h1 className={classes.title}>
                    {documentData.name}
                    <button
                        type="button"
                        className={classes.scrollToTop}
                        onClick={scrollToTop}
                        aria-label="Scroll to top"
                    >
                        <IconArrowBarToUp />
                    </button>
                </h1>
                {hasDescription && (
                    <div className={classes.description}>
                        {renderDescriptionContent(documentData)}
                    </div>
                )}
                <div className={classes.content}>
                    {renderMainContent(documentData)}
                </div>
            </>
        );
    };

    return (
        <div className={layoutClasses.page}>
            <nav className={layoutClasses.sidebar}>
                {showToc && tocDocument && (
                    <TableOfContents
                        key={segmentId}
                        document={tocDocument}
                        activeSegmentId={
                            isDocumentFetching ? segmentId : undefined
                        }
                        clickedId={clickedTocId}
                        onClickId={setClickedTocId}
                    />
                )}
            </nav>

            <article ref={articleRef} className={layoutClasses.article}>
                <div className={layoutClasses.articleInner}>
                    {getArticleContent()}
                </div>
            </article>

            <aside
                className={`${layoutClasses.onThisPage} ${isSidebarCollapsed ? layoutClasses.onThisPageCollapsed : ""} ${!isDesktop ? layoutClasses.onThisPageMobile : ""}`}
                style={
                    isSidebarCollapsed
                        ? {width: 0, flexBasis: 0}
                        : undefined
                }
            >
                <Tooltip
                    label={
                        isSidebarCollapsed
                            ? t("common.expandSidebar")
                            : t("common.collapseSidebar")
                    }
                    position="left"
                >
                    <ActionIcon
                        className={layoutClasses.sidebarToggle}
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={toggleSidebar}
                    >
                        {isSidebarCollapsed ? (
                            <IconArrowLeft />
                        ) : (
                            <IconArrowRight />
                        )}
                    </ActionIcon>
                </Tooltip>
                <Box className={layoutClasses.onThisPageContent}>
                    {(!isDocumentFetching || isIntraTopicFetch) && (
                        <>
                            <SupplierInfo
                                resourceId={resourceId}
                                segmentId={segmentId}
                            />
                            <RelatedContent
                                resourceId={resourceId}
                                segmentId={segmentId}
                            />
                        </>
                    )}
                </Box>
            </aside>
        </div>
    );
};
